import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Smartphone, Mail, Phone, MessageCircle, Headset } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { mockData } from '../mock';

const channelIcons = {
  WhatsApp: MessageCircle,
  RCS: MessageCircle,
  SMS: Smartphone,
  Email: Mail,
  Voz: Phone,
  URA: Phone,
  'Agentes Virtuais': Headset
};

const Product = () => {
  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-purple-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Orquestrador CRM Completo
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Gerencie todas as suas comunicações em uma única plataforma inteligente e automatizada
          </p>
        </div>
      </section>

      {/* Main Features Tabs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="orchestration" className="w-full">
            <TabsList className="grid w-full grid-cols-1 md:grid-cols-3 mb-12 h-auto">
              <TabsTrigger value="orchestration" className="text-base py-3">
                Orquestração
              </TabsTrigger>
              <TabsTrigger value="channels" className="text-base py-3">
                Canais
              </TabsTrigger>
              <TabsTrigger value="automation" className="text-base py-3">
                Automação
              </TabsTrigger>
            </TabsList>

            <TabsContent value="orchestration">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    {mockData.productFeatures.orchestration.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">
                    {mockData.productFeatures.orchestration.description}
                  </p>
                  <ul className="space-y-4">
                    {mockData.productFeatures.orchestration.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-base">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contato">
                    <Button className="mt-8 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
                      Conhecer Mais
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
                <div className="bg-white rounded-2xl shadow-lg p-8 h-96 flex items-center justify-center overflow-hidden border-2 border-gray-100">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_manubot-connect/artifacts/y23fdr15_jornada_2.png"
                    alt="Jornadas Personalizadas - Interface de Orquestração"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="channels">
              <div>
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    {mockData.productFeatures.channels.title}
                  </h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    {mockData.productFeatures.channels.description}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {mockData.productFeatures.channels.channels.map((channel, index) => {
                    const Icon = channelIcons[channel.name];
                    return (
                      <Card key={index} className="text-center border-2 hover:border-blue-600 hover:shadow-lg transition-all duration-300">
                        <CardHeader>
                          <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Icon className="h-8 w-8 text-blue-600" />
                          </div>
                          <CardTitle>{channel.name}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <CardDescription className="text-sm">{channel.provider}</CardDescription>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="automation">
              <div>
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    {mockData.productFeatures.automation.title}
                  </h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
                    {mockData.productFeatures.automation.description}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  {mockData.productFeatures.automation.stats.map((stat, index) => (
                    <Card key={index} className="text-center border-2">
                      <CardHeader>
                        <CardTitle className="text-4xl font-bold text-blue-600 mb-2">
                          {stat.value}
                        </CardTitle>
                        <CardDescription className="text-base">{stat.label}</CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
                <div className="text-center">
                  <Link to="/contato">
                    <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
                      Solicitar Demonstração
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Integration Section - Multifornecedor em Destaque */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6">
              <span className="text-white font-semibold text-lg">⭐ Diferencial Competitivo</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Multifornecedor: Flexibilidade e Performance
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Escolha um ou mais fornecedores por canal e avalie o desempenho de entrega e conversão
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold mb-3">Escolha Livre</h3>
              <p className="text-blue-100">
                Conecte múltiplos fornecedores para cada canal (SMS, WhatsApp, Email, RCS, URA) e tenha total autonomia
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
              <div className="text-5xl mb-4">📊</div>
              <h3 className="text-2xl font-bold mb-3">Análise Comparativa</h3>
              <p className="text-blue-100">
                Avalie desempenho de entrega e conversão por canal e fornecedor com dashboards customizados
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
              <div className="text-5xl mb-4">⚡</div>
              <h3 className="text-2xl font-bold mb-3">Otimização Contínua</h3>
              <p className="text-blue-100">
                Identifique os melhores fornecedores e otimize custos sem perder performance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboards Customizados Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Dashboards Customizados
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Visualize todos os dados que importam para sua operação em tempo real
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">Performance por Canal</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Taxa de entrega por canal</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Taxa de conversão por canal</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Tempo médio de resposta</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Custo por conversão</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">Performance por Fornecedor</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Comparativo entre fornecedores</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">SLA de entrega por fornecedor</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Análise de custos operacionais</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Relatórios personalizados</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Original Integration Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Integração Total e Flexível
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Todos os canais são conectados via fornecedores para máxima flexibilidade
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">Multifornecedores</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">WhatsApp via Meta BSP oficial</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">RCS Google e SMS via múltiplos fornecedores</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Email, Voz e URA integrados</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Escolha livre de fornecedores por canal</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">Controle Total</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Dashboards customizados em tempo real</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Análise comparativa de fornecedores</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Métricas de conversão por canal</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Histórico completo de interações</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Comece a orquestrar suas jornadas hoje
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Fale com nossos especialistas e descubra como a Manubot pode transformar seu CRM
          </p>
          <Link to="/contato">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
              Solicitar Demonstração
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Product;